import { EchoFilterRequest } from '@/js/services/api/filter/types';
import { RequestPostScheduleCoachJob } from '@/js/services/api/scheduleCoachingForm/types';
import {
    ScheduleCoachingFormData,
    ScheduleCoachingFormInterval,
} from '@/js/stores/scheduleCoaching';
import { Frequency, RRule, Weekday, WeekdayStr } from 'rrule';

export function shouldUseLastDayOfMonth(interval: ScheduleCoachingFormInterval): boolean {
    if (interval.frequency !== Frequency.MONTHLY) return false;
    const day = Number(interval.startDate.split('-')[2]);
    return day >= 29 && day <= 31;
}

export function formatIntervalToRRule(interval: ScheduleCoachingFormInterval): string {
    const rrule = new RRule({
        freq: interval.frequency,
        interval: interval.interval,
        byweekday:
            interval.frequency === Frequency.WEEKLY
                ? Object.entries(interval.byweekday)
                      .filter(([_, isSelected]) => isSelected)
                      .map(([day, _]) => {
                          return new Weekday(RRule[day as WeekdayStr].weekday, 1);
                      })
                : undefined,
        bymonthday: shouldUseLastDayOfMonth(interval) ? -1 : undefined,
    });
    const rruleStr = rrule.toString();
    return rruleStr.startsWith('RRULE:') ? rruleStr.slice(6) : rruleStr;
}

export function formatRRuleToInterval(
    rruleStr: string
): Omit<ScheduleCoachingFormInterval, 'startDate' | 'endDate'> {
    const rrule = RRule.fromString(rruleStr);

    const interval = rrule.origOptions.interval;
    const frequency = rrule.origOptions.freq;

    const weekdays = rrule.origOptions.byweekday as Weekday[] | null;

    const byweekday: Record<WeekdayStr, boolean> = {
        MO: false,
        TU: false,
        WE: false,
        TH: false,
        FR: false,
        SA: false,
        SU: false,
    };

    if (weekdays && weekdays.length > 0) {
        weekdays.forEach((weekday) => {
            if (weekday.weekday === 0) {
                byweekday.MO = true;
            } else if (weekday.weekday === 1) {
                byweekday.TU = true;
            } else if (weekday.weekday === 2) {
                byweekday.WE = true;
            } else if (weekday.weekday === 3) {
                byweekday.TH = true;
            } else if (weekday.weekday === 4) {
                byweekday.FR = true;
            } else if (weekday.weekday === 5) {
                byweekday.SA = true;
            } else if (weekday.weekday === 6) {
                byweekday.SU = true;
            }
        });
    }

    return {
        frequency: frequency || 1,
        interval: interval || 1,
        byweekday,
    };
}

export const createScheduleCoachingRequest = (
    formData: ScheduleCoachingFormData,
    filter: EchoFilterRequest
): RequestPostScheduleCoachJob => {
    return {
        totalCoachingOccasions: 1,
        visibilityMode: formData.visibilityMode,
        title: formData.title,
        description: formData.description,
        formId: formData.formId,
        interactionsPerRun: formData.interactionsPerRun,
        intervals: formData.intervals.map((interval) => {
            return {
                rRule: formatIntervalToRRule(interval),
                startDate: interval.startDate,
                endDate: interval.endDate || null,
            };
        }),
        filter,
    };
};
